import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  MenuItem,
  Pagination,
  FormControlLabel,
  Switch,
  Paper,
  IconButton,
  Tooltip,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PreviewIcon from '@mui/icons-material/Visibility';
import EditNoteIcon from '@mui/icons-material/EditNote';
import RefreshIcon from '@mui/icons-material/Refresh';
import SportsCricketIcon from '@mui/icons-material/SportsCricket';
import { Grid } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { matchApi, tournamentApi } from '../services/api';
import SeriesAutocomplete from '../components/SeriesAutocomplete';
import TeamAutocomplete from '../components/TeamAutocomplete';
import GroundAutocomplete from '../components/GroundAutocomplete';
import { Match, Series, Venue, Team, Ground, PaginatedResponse } from '../models';

interface FilterState {
  format: string;
  status: string;
  series: Series | null;
  team: Team | null;
  ground: Ground | null;
  ordering: string;
  from_date?: string;
  to_date?: string;
}

interface MatchFormValues {
  id: number;
  series: Series;
  match_format: string;
  match_status: string;
  start_date: string;
  lineup_announced: boolean;
  venue: { id: number } | null;
  match_result: string | null;
  score_algorithm_enabled: boolean;
  fp_available: boolean;
  is_substitute_allowed: boolean;
  is_e_coverage: boolean;
}

const validationSchema = yup.object({
  series: yup.object().shape({
    id: yup.number().required('Series is required'),
  }).required('Series is required'),
  match_format: yup.string().required('Match format is required'),
  match_status: yup.string().required('Match status is required'),
  start_date: yup.string().required('Start date is required'),
  lineup_announced: yup.boolean(),
  venue: yup.object().shape({
    id: yup.number().required('Venue is required'),
  }).nullable(),
  match_result: yup.string().nullable(),
  score_algorithm_enabled: yup.boolean(),
  fp_available: yup.boolean(),
  is_substitute_allowed: yup.boolean(),
  is_e_coverage: yup.boolean(),
});

const statusOptions = [
  { value: 'Not Started', label: 'Not Started' },
  { value: 'In Progress', label: 'In Progress' },
  { value: 'Completed', label: 'Completed' },
  { value: 'Abandoned', label: 'Abandoned' },
  { value: 'Cancelled', label: 'Cancelled' },
];

const matchFormatOptions = [
  { value: 'T20', label: 'T20' },
  { value: 'T10', label: 'T10' },
  { value: 'ODI', label: 'ODI' },
  { value: 'TEST', label: 'Test' },
  { value: 'HUNDRED_BALL', label: 'Hundred' },
  { value: 'SIXES', label: 'Sixes' },
];

const Matches: React.FC = () => {
  const [matches, setMatches] = useState<Match[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [selectedMatch, setSelectedMatch] = useState<Match | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [totalCount, setTotalCount] = useState(0);
  const queryParams = new URLSearchParams(window?.location.search);
  const statusParam = queryParams.get("status");
  const seriesId = queryParams.get("series");
  const teamId = queryParams.get("team");
  const groundId = queryParams.get("ground");
  
  const [filters, setFilters] = useState<FilterState>({
    format: '',
    status: statusParam || '',
    series: null,
    team: null,
    ground: null,
    ordering: '',
  });

  // Check URL parameters when component mounts
  useEffect(() => {
    const fetchInitialFilters = async () => {
      try {
        setLoading(true);
        const filtersToUpdate = { ...filters };
        
        // Fetch series details if seriesId is in the URL
        if (seriesId) {
          try {
            const seriesResponse = await tournamentApi.getById(Number(seriesId));
            if (seriesResponse.data) {
              filtersToUpdate.series = seriesResponse.data;
            }
          } catch (error) {
            console.error('Error fetching series details:', error);
          }
        }
        
        // Fetch team details if teamId is in the URL
        if (teamId) {
          try {
            const { teamApi } = await import('../services/api');
            const teamResponse = await teamApi.getById(Number(teamId));
            if (teamResponse.data) {
              filtersToUpdate.team = teamResponse.data;
            }
          } catch (error) {
            console.error('Error fetching team details:', error);
          }
        }
        
        // Fetch ground details if groundId is in the URL
        if (groundId) {
          try {
            const { groundApi } = await import('../services/api');
            const groundResponse = await groundApi.getById(Number(groundId));
            if (groundResponse.data) {
              filtersToUpdate.ground = groundResponse.data;
            }
          } catch (error) {
            console.error('Error fetching ground details:', error);
          }
        }
        
        setFilters(filtersToUpdate);
      } catch (error) {
        console.error('Error fetching initial filters:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchInitialFilters();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seriesId, teamId, groundId]);  // Dependencies for the URL parameters

  const columns: GridColDef[] = [
    { 
      field: 'id', 
      headerName: 'ID', 
      width: 70, 
      sortable: true,
      filterable: true,
      type: 'string'
    },
    { 
      field: 'name', 
      headerName: 'Name', 
      width: 150, 
      sortable: true,
      filterable: false
    },
    {
      field: 'd_id',
      headerName: 'D ID',
      width: 70,
      sortable: true,
      filterable: true,
      type: 'string',
      hideable: true,
    },
    {
      field: 'e_id',
      headerName: 'E ID',
      width: 70,
      sortable: true,
      filterable: true,
      type: 'string',
      hideable: true,
    },
    {
      field: 'mt_id',
      headerName: 'MT ID',
      width: 70,
      sortable: true,
      filterable: true,
      type: 'string',
      hideable: true,
    },
    {
      field: 'c_id',
      headerName: 'C ID',
      width: 70,
      sortable: true,
      filterable: true,
      type: 'string',
      hideable: true,
    },
    {
      field: 'cb_id',
      headerName: 'CB ID',
      width: 70,
      sortable: true,
      filterable: true,
      type: 'string',
      hideable: true,
    },
    { 
      field: 'series',
      headerName: 'Series',
      width: 200,
      filterable: true,
      valueGetter: (series: Series) => series?.name || '',
    },
    { 
      field: 'match_format', 
      headerName: 'Format', 
      width: 70,
      filterable: true,
      type: 'singleSelect',
      valueOptions: matchFormatOptions.map(opt => opt.value)
    },
    { 
      field: 'start_date', 
      headerName: 'Date', 
      width: 170, 
      sortable: true,
      type: 'dateTime',
      valueGetter: (params: any) => new Date(params),
    },
    { 
      field: 'venue', 
      headerName: 'Venue', 
      width: 200, 
      valueGetter: (venue: Venue) => venue?.name
    },
    {
      field: 'match_status',
      headerName: 'Status',
      width: 100,
      sortable: true,
      filterable: true,
      type: 'singleSelect',
      valueOptions: statusOptions.map(opt => opt.value),
      renderCell: (params) => (
        <Box
          sx={{
            backgroundColor:
              params.value === 'In Progress'
                ? '#ff9800'
                : params.value === 'Completed'
                ? '#4caf50'
                : params.value === 'Abandoned' || params.value === 'Cancelled'
                ? '#f44336'
                : '#2196f3',
            color: 'white',
            padding: '2px 6px',
            fontSize: '0.75rem',
            borderRadius: '10px',
            textTransform: 'capitalize',
            display: 'inline-block',
            minWidth: '60px',
            textAlign: 'center',
          }}
        >
          {params.value}
        </Box>
      ),
    },
    { 
      field: 'match_result', 
      headerName: 'Result', 
      width: 200, 
      sortable: true,
      filterable: false
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 250,
      sortable: false,
      filterable: false,
      renderCell: (params: any) => (
        <Box>
          <Tooltip title="Edit">
            <IconButton
              color="primary"
              size="small"
              onClick={() => handleEdit(params.row)}
            >
              <EditIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton
              color="error"
              size="small"
              onClick={() => handleDelete(params.row.id)}
            >
              <DeleteIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Content Editor">
            <IconButton
              color="secondary"
              size="small"
              onClick={() => window.location.href = `/matches/${params.row.id}/content`}
            >
              <EditNoteIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Preview">
            <IconButton
              color="info"
              size="small"
              onClick={() => window.open(`https://www.ai11.in/series/${params.row.series?.slug}/match/${params.row.slug}`, '_blank')}
            >
              <PreviewIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Match Players">
            <IconButton
              color="success"
              size="small"
              onClick={() => window.location.href = `/match-players?match=${params.row.id}`}
            >
              <SportsCricketIcon />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  const formik = useFormik<MatchFormValues>({
    initialValues: {
      id: 0,
      series: {} as Series,
      match_format: 'T20',
      match_status: 'Not Started',
      start_date: '',
      lineup_announced: false,
      venue: null,
      match_result: null,
      score_algorithm_enabled: false,
      fp_available: false,
      is_substitute_allowed: false,
      is_e_coverage: true,
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        if (selectedMatch) {
          await matchApi.update(selectedMatch.id, values);
        } else {
          await matchApi.create(values);
        }
        fetchMatches(page, pageSize);
        handleClose();
      } catch (error) {
        console.error('Error saving match:', error);
      }
    },
  });

  const fetchMatches = React.useCallback(async (pageNumber: number = 1, size: number = pageSize) => {
    try {
      setLoading(true);
      const params: any = {
        page: pageNumber,
        page_size: size,
      };

      if (filters.format) {
        params.match_format = filters.format;
      }
      if (filters.status) {
        params.match_status = filters.status;
      }
      if (filters.series) {
        params.series = filters.series.id;
      }
      if (filters.team) {
        params.teams = filters.team.id;
      }
      if (filters.ground) {
        params.venue = filters.ground.id;
      }
      if (filters.ordering) {
        params.ordering = filters.ordering;
      }
      if (filters.from_date) {
        params.from_date = filters.from_date;
      }
      if (filters.to_date) {
        params.to_date = filters.to_date;
      }

      const response = await matchApi.getAll(params);
      const data = response.data as PaginatedResponse<Match>;
      setMatches(data.results);
      setTotalCount(data.count);
    } catch (error) {
      console.error('Error fetching matches:', error);
    } finally {
      setLoading(false);
    }
  }, [filters, pageSize]);

  const handleSortModelChange = (model: any) => {
    if (model.length > 0) {
      const { field, sort } = model[0];
      const ordering = sort === 'desc' ? `-${field}` : field;
      setFilters(prev => ({
        ...prev,
        ordering
      }));
    } else {
      setFilters(prev => ({
        ...prev,
        ordering: ''
      }));
    }
  };

  const handleFilterModelChange = (model: any) => {
    const newFilters = { ...filters };
    
    if (model.items) {
      model.items.forEach((item: any) => {
        if (item.field === 'start_date') {
          if (item.operator === '>=') {
            newFilters.from_date = item.value;
          } else if (item.operator === '<=') {
            newFilters.to_date = item.value;
          }
        } else if (item.field === 'match_format') {
          newFilters.format = item.value;
        } else if (item.field === 'match_status') {
          newFilters.status = item.value;
        }
      });
    }

    setFilters(newFilters);
  };

  useEffect(() => {
    fetchMatches(page, pageSize);
  }, [page, pageSize, filters, fetchMatches]);

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  const handleFilterChange = (name: keyof FilterState, value: any) => {
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
    setPage(1); // Reset to first page when filters change
    
    // Update URL with query parameters for deep linking
    const searchParams = new URLSearchParams(window.location.search);
    
    if (name === 'series' && value) {
      searchParams.set('series', value.id.toString());
    } else if (name === 'series' && !value) {
      searchParams.delete('series');
    }
    
    if (name === 'team' && value) {
      searchParams.set('team', value.id.toString());
    } else if (name === 'team' && !value) {
      searchParams.delete('team');
    }
    
    if (name === 'ground' && value) {
      searchParams.set('ground', value.id.toString());
    } else if (name === 'ground' && !value) {
      searchParams.delete('ground');
    }
    
    if (name === 'status' && value) {
      searchParams.set('status', value);
    } else if (name === 'status' && !value) {
      searchParams.delete('status');
    }
    
    const newUrl = window.location.pathname + (searchParams.toString() ? `?${searchParams.toString()}` : '');
    window.history.pushState({}, '', newUrl);
  };

  const handleEdit = (match: Match) => {
    setSelectedMatch(match);
    formik.setValues({
      id: match.id,
      series: match.series,
      match_format: match.match_format,
      match_status: match.match_status,
      start_date: match.start_date,
      lineup_announced: match.lineup_announced,
      venue: match.venue ? { id: match.venue.id } : null,
      match_result: match.match_result,
      score_algorithm_enabled: match.score_algorithm_enabled,
      fp_available: match.fp_available,
      is_substitute_allowed: false,
      is_e_coverage: true,
    });
    setOpen(true);
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this match?')) {
      try {
        await matchApi.delete(id);
        fetchMatches(page, pageSize);
      } catch (error) {
        console.error('Error deleting match:', error);
      }
    }
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedMatch(null);
    formik.resetForm();
  };

  const handleRefresh = () => {
    fetchMatches(page, pageSize);
  };

  return (
    <Box sx={{ height: "calc(100vh - 320px)", width: "100%" }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h4">Matches</Typography>
        <Box>
          <Tooltip title="Refresh Data">
            <IconButton 
              color="primary" 
              onClick={handleRefresh} 
              sx={{ mr: 1 }}
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Button variant="contained" color="primary" onClick={() => setOpen(true)}>
            Add Match
          </Button>
        </Box>
      </Box>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <SeriesAutocomplete
              value={filters.series}
              onChange={(value) => handleFilterChange('series', value)}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TeamAutocomplete
              value={filters.team}
              onChange={(value) => handleFilterChange('team', value)}
              seriesId={filters.series?.id || null}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <GroundAutocomplete
              value={filters.ground}
              onChange={(value) => handleFilterChange('ground', value)}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              select
              label="Status"
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              variant="outlined"
              size="small"
            >
              <MenuItem value="">All Statuses</MenuItem>
              {statusOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>
      </Paper>

      <DataGrid
        rows={matches}
        columns={columns}
        initialState={{
          pagination: {
            paginationModel: { pageSize: 50, page: 0 },
          },
          columns: {
            columnVisibilityModel: {
              d_id: false,
              e_id: false,
              mt_id: false,
              c_id: false,
              cb_id: false,
              match_result: false,
            },
          },
        }}
        pageSizeOptions={[10, 50, 100]}
        checkboxSelection
        disableRowSelectionOnClick
        loading={loading}
        rowCount={totalCount}
        paginationMode="server"
        sortingMode="server"
        filterMode="server"
        onSortModelChange={handleSortModelChange}
        onFilterModelChange={handleFilterModelChange}
        onPaginationModelChange={(model) => {
          setPage(model.page + 1);
          setPageSize(model.pageSize);
        }}
        sx={{
          '& .MuiDataGrid-cell': {
            py: 0.5, // Reduce vertical padding in cells
          },
          '& .MuiDataGrid-columnHeaders': {
            backgroundColor: '#f5f5f5', // Light gray background for header
          },
          '& .MuiDataGrid-row:hover': {
            backgroundColor: '#f0f7ff', // Light blue on hover
          },
          '& .MuiDataGrid-virtualScroller': {
            overflowX: 'scroll',
          }
        }}
        density="compact"
      />

      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
        <Pagination
          count={Math.ceil(totalCount / pageSize)}
          page={page}
          onChange={handlePageChange}
          color="primary"
        />
      </Box>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          {selectedMatch ? 'Edit Match' : 'Add Match'}
        </DialogTitle>
        <form onSubmit={formik.handleSubmit}>
          <DialogContent>
            <TextField
              fullWidth
              margin="normal"
              name="series.id"
              label="Series ID"
              type="number"
              value={formik.values.series?.id || ''}
              onChange={formik.handleChange}
              error={Boolean(formik.errors.series)}
              helperText={typeof formik.errors.series === 'string' ? formik.errors.series : ''}
            />
            <TextField
              fullWidth
              margin="normal"
              name="match_format"
              label="Match Format"
              select
              value={formik.values.match_format}
              onChange={formik.handleChange}
              error={formik.touched.match_format && Boolean(formik.errors.match_format)}
              helperText={formik.touched.match_format && formik.errors.match_format}
            >
              {matchFormatOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              fullWidth
              margin="normal"
              name="match_status"
              label="Status"
              select
              value={formik.values.match_status}
              onChange={formik.handleChange}
              error={formik.touched.match_status && Boolean(formik.errors.match_status)}
              helperText={formik.touched.match_status && formik.errors.match_status}
            >
              {statusOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              fullWidth
              margin="normal"
              name="start_date"
              label="Start Date"
              type="datetime-local"
              value={formik.values.start_date}
              onChange={formik.handleChange}
              error={formik.touched.start_date && Boolean(formik.errors.start_date)}
              helperText={formik.touched.start_date && formik.errors.start_date}
              InputLabelProps={{
                shrink: true,
              }}
            />
            <TextField
              fullWidth
              margin="normal"
              name="venue.id"
              label="Venue ID"
              type="number"
              value={formik.values.venue?.id || ''}
              onChange={formik.handleChange}
              error={Boolean(formik.errors.venue)}
              helperText={typeof formik.errors.venue === 'string' ? formik.errors.venue : ''}
            />
            <TextField
              fullWidth
              margin="normal"
              name="match_result"
              label="Result"
              value={formik.values.match_result || ''}
              onChange={formik.handleChange}
              error={formik.touched.match_result && Boolean(formik.errors.match_result)}
              helperText={formik.touched.match_result && formik.errors.match_result}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formik.values.lineup_announced}
                  onChange={formik.handleChange}
                  name="lineup_announced"
                />
              }
              label="Lineup Announced"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formik.values.score_algorithm_enabled}
                  onChange={formik.handleChange}
                  name="score_algorithm_enabled"
                />
              }
              label="Score Algorithm Enabled"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formik.values.fp_available}
                  onChange={formik.handleChange}
                  name="fp_available"
                />
              }
              label="FP Available"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formik.values.is_substitute_allowed}
                  onChange={formik.handleChange}
                  name="is_substitute_allowed"
                />
              }
              label="Substitute Allowed"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formik.values.is_e_coverage}
                  onChange={formik.handleChange}
                  name="is_e_coverage"
                />
              }
              label="E Coverage"
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose}>Cancel</Button>
            <Button type="submit" variant="contained" color="primary">
              {selectedMatch ? 'Update' : 'Add'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};

export default Matches;